import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Privacy from "@/Privacy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EASE = [0.22, 1, 0.36, 1];
const ROLES = ["Dater", "Matchmaker", "Other"];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

const Wordmark = ({ small, testId }) => (
  <span data-testid={testId} className={small ? "wordmark wordmark-small" : "wordmark"}>
    READY FOR THE CHECK
  </span>
);

const Field = ({ label, testId, error, errorTestId, ...inputProps }) => (
  <div className="field">
    <label className="label" htmlFor={testId}>
      {label}
    </label>
    <input id={testId} data-testid={testId} className="input" {...inputProps} />
    {error && (
      <p className="field-error" data-testid={errorTestId}>
        {error}
      </p>
    )}
  </div>
);

function WaitlistForm() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    market: "",
    age: "",
    occupation: "",
    role: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({ age: "", email: "", form: "" });

  const ageNum = form.age === "" ? NaN : Number(form.age);
  const complete =
    form.first_name.trim() &&
    form.last_name.trim() &&
    EMAIL_RE.test(form.email.trim()) &&
    form.market.trim() &&
    Number.isFinite(ageNum) &&
    ageNum > 0 &&
    form.occupation.trim() &&
    form.role;

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors({ age: "", email: "", form: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ age: "", email: "", form: "" });
    if (ageNum < 18) {
      setErrors({ age: "You must be 18 or older to join", email: "", form: "" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        market: form.market,
        age: ageNum,
        occupation: form.occupation,
        role: form.role,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      if (err.response?.status === 409) {
        setErrors({ age: "", email: message, form: "" });
      } else if (err.response?.status === 400 && message.includes("18")) {
        setErrors({ age: message, email: "", form: "" });
      } else {
        setErrors({ age: "", email: "", form: message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.p
        className="success-message"
        data-testid="success-message"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
      >
        You're on the list. We'll let you know the moment we launch.
      </motion.p>
    );
  }

  return (
    <form data-testid="waitlist-form" onSubmit={handleSubmit} noValidate>
      <p className="form-intro">Tell us a little about yourself. It takes less than a minute.</p>
      <div className="field-grid">
        <Field label="First name" testId="first-name-input" type="text" autoComplete="given-name" value={form.first_name} onChange={set("first_name")} />
        <Field label="Last name" testId="last-name-input" type="text" autoComplete="family-name" value={form.last_name} onChange={set("last_name")} />
      </div>
      <Field
        label="Email"
        testId="email-input"
        type="email"
        autoComplete="email"
        value={form.email}
        onChange={set("email")}
        error={errors.email}
        errorTestId="duplicate-email-message"
      />
      <div className="field-grid">
        <Field label="City or region" testId="city-input" type="text" autoComplete="address-level2" value={form.market} onChange={set("market")} />
        <Field
          label="Age"
          testId="age-input"
          type="number"
          min="0"
          inputMode="numeric"
          value={form.age}
          onChange={set("age")}
          error={errors.age}
          errorTestId="age-error-message"
        />
      </div>
      <Field label="Occupation" testId="occupation-input" type="text" autoComplete="organization-title" value={form.occupation} onChange={set("occupation")} />
      <fieldset className="field">
        <legend className="label">I am a...</legend>
        <div className="radio-row" data-testid="role-radio-group">
          {ROLES.map((role) => (
            <label key={role} className={form.role === role ? "radio radio-checked" : "radio"}>
              <input
                type="radio"
                name="role"
                value={role}
                checked={form.role === role}
                onChange={set("role")}
                data-testid={`role-radio-${role.toLowerCase()}`}
              />
              <span className="radio-dot" aria-hidden="true" />
              {role}
            </label>
          ))}
        </div>
      </fieldset>
      {errors.form && (
        <p className="field-error" data-testid="form-error-message">
          {errors.form}
        </p>
      )}
      <button
        type="submit"
        className="submit-button"
        data-testid="join-waitlist-button"
        disabled={!complete || submitting}
      >
        Join the waitlist
      </button>
      <p className="trust-line">No spam. No public listing. Delete your data anytime.</p>
    </form>
  );
}

function Landing() {
  return (
    <div className="page" data-testid="landing-page">
      <motion.header className="header" {...reveal(0)}>
        <Wordmark testId="wordmark-header" />
      </motion.header>

      <main className="main">
        <motion.p className="eyebrow" {...reveal(0.15)}>
          For people with something to lose
        </motion.p>

        <div className="headline-mask">
          <motion.h1
            className="headline"
            data-testid="headline"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
          >
            Ready for the check.
          </motion.h1>
        </div>

        <motion.p className="subhead" {...reveal(0.45)}>
          Mutual verification for dating, built for people whose name is already searchable.
        </motion.p>

        <motion.p className="mutual-line" {...reveal(0.55)}>
          Know who you're meeting — and be known in return. Nothing shared without both of you agreeing.
        </motion.p>

        <motion.div className="card-who-row" data-testid="card-who-row" {...reveal(0.65)}>
          <img
            src="/verification-card.png"
            alt="Example verified profile card for Alexandra Voss: identity verified, records reviewed, references confirmed"
            className="card-image"
            data-testid="verification-card-image"
          />
          <div className="who-section" data-testid="who-section">
            <p className="who-eyebrow">Who this is for</p>
            <p className="who-line">
              A founder or executive who's easy to look up — and doesn't want a first date to turn
              into due diligence.
            </p>
            <p className="who-line">
              Someone who met through an app, a mutual friend, or a matchmaker, and wants both
              sides walking in with equal information.
            </p>
            <p className="who-line">
              Anyone who's been burned before by finding out too late who they were actually
              meeting.
            </p>
          </div>
        </motion.div>

        <motion.p className="closing-line" {...reveal(0.75)}>
          Respect, discretion, and trust — settled before you sit down, so the date itself can be
          nothing more than a date. That's what it means to be ready for the check.
        </motion.p>

        <motion.p className="form-hint" {...reveal(0.85)}>
          ↓ Application form below ↓
        </motion.p>

        <motion.div className="form-card" data-testid="form-card" {...reveal(0.95)}>
          <WaitlistForm />
        </motion.div>
      </main>

      <motion.footer className="footer" data-testid="footer" {...reveal(0.9)}>
        <Wordmark small testId="wordmark-footer" />
        <span className="footer-sep" aria-hidden="true">·</span>
        <a className="footer-link" href="/privacy" data-testid="privacy-link">
          Privacy Policy
        </a>
        <span className="footer-sep" aria-hidden="true">·</span>
        <a className="footer-link" href="mailto:readyforthecheck@freddymedia.com" data-testid="contact-link">
          Contact
        </a>
        <span className="footer-sep" aria-hidden="true">·</span>
        <span className="footer-copy">© 2026 Ready for the Check</span>
      </motion.footer>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
      </Routes>
    </BrowserRouter>
  );
}
